import React, { useMemo, useRef, useState } from 'react'
import type { CodeItemSubgroup } from '../../types/codeIndex'
import { getCompositeGroupStructureErrors, normalizeCompositeSubgroups } from '../../utils/compositeCodeUtils'

type CompositeGroupEditorProps = {
  digits: number
  subgroups: CodeItemSubgroup[]
  onChange: (subgroups: CodeItemSubgroup[]) => void
  disabled?: boolean
  autoFocusAdd?: boolean
  compact?: boolean
}

function createLocalKey(): string {
  return `subgroup-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export const CompositeGroupEditor: React.FC<CompositeGroupEditorProps> = ({
  digits,
  subgroups,
  onChange,
  disabled = false,
  autoFocusAdd = false,
  compact = false,
}) => {
  const [name, setName] = useState('')
  const [length, setLength] = useState(1)
  const [anyPosition, setAnyPosition] = useState(false)
  const [draggedKey, setDraggedKey] = useState<string | null>(null)
  const nameInputRef = useRef<HTMLInputElement | null>(null)
  const normalizedDigits = Math.max(1, Math.floor(Number(digits)) || 1)
  const positioned = useMemo(
    () => normalizeCompositeSubgroups(subgroups, normalizedDigits),
    [normalizedDigits, subgroups]
  )
  const errors = useMemo(
    () => getCompositeGroupStructureErrors(subgroups, normalizedDigits),
    [normalizedDigits, subgroups]
  )
  const fixedLength = subgroups.reduce((total, subgroup) => (
    subgroup.positionMode === 'any' ? total : total + Math.max(1, Math.floor(Number(subgroup.length)) || 1)
  ), 0)
  const hasFixedGroups = subgroups.some(subgroup => subgroup.positionMode !== 'any')

  React.useEffect(() => {
    if (!autoFocusAdd) return
    const timeout = window.setTimeout(() => nameInputRef.current?.focus(), 0)
    return () => window.clearTimeout(timeout)
  }, [autoFocusAdd])

  const addGroup = () => {
    const nextName = name.trim()
    if (!nextName || subgroups.some(subgroup => subgroup.name.trim().toLowerCase() === nextName.toLowerCase())) return
    onChange([
      ...subgroups,
      {
        localKey: createLocalKey(),
        name: nextName,
        length: anyPosition ? normalizedDigits : Math.max(1, Math.min(normalizedDigits, Math.floor(length) || 1)),
        positionMode: anyPosition ? 'any' : 'fixed',
      },
    ])
    setName('')
    setLength(1)
    setAnyPosition(false)
    nameInputRef.current?.focus()
  }

  const move = (index: number, destination: number) => {
    if (destination < 0 || destination >= subgroups.length || destination === index) return
    const next = [...subgroups]
    const [item] = next.splice(index, 1)
    next.splice(destination, 0, item)
    onChange(next)
  }

  const update = (localKey: string, patch: Partial<CodeItemSubgroup>) => {
    onChange(subgroups.map(subgroup => subgroup.localKey === localKey ? { ...subgroup, ...patch } : subgroup))
  }

  return (
    <div className={compact ? 'rounded border border-[#D6E4F2] bg-[#F8FBFF] p-2' : ''}>
      <div className={`grid items-end gap-2 ${compact ? 'grid-cols-[minmax(0,1fr)_4rem_auto]' : 'grid-cols-[minmax(180px,1fr)_7rem_7rem_auto]'}`}>
        <label className="flex min-w-0 flex-col gap-1 text-xs font-medium text-gray-600">
          Group name
          <input
            ref={nameInputRef}
            type="text"
            value={name}
            disabled={disabled}
            onChange={event => setName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault()
                addGroup()
              }
            }}
            placeholder="Group name"
            aria-label={compact ? 'Subgroup name' : 'New composite group name'}
            className="min-w-0 rounded border border-[#B7C6D8] bg-white px-2 py-1.5 text-sm font-normal"
          />
        </label>
        {!compact && (
          <label className="flex h-[30px] items-center gap-2 whitespace-nowrap text-xs text-gray-600">
            <input
              type="checkbox"
              checked={anyPosition}
              disabled={disabled}
              onChange={event => setAnyPosition(event.target.checked)}
              className="accent-[#1D74D8]"
            />
            Any position
          </label>
        )}
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
          Digits
          <input
            type="number"
            min={1}
            max={normalizedDigits}
            value={anyPosition ? normalizedDigits : length}
            disabled={disabled || anyPosition}
            onChange={event => setLength(Math.max(1, Math.min(normalizedDigits, Number(event.target.value) || 1)))}
            aria-label={compact ? 'Subgroup digits' : 'New composite group digits'}
            className="w-full rounded border border-[#B7C6D8] bg-white px-2 py-1.5 text-sm font-normal disabled:bg-gray-100"
          />
        </label>
        <button
          type="button"
          disabled={disabled || !name.trim()}
          onClick={addGroup}
          className="h-[30px] rounded bg-[#1D74D8] px-3 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add
        </button>
      </div>
      {compact && (
        <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={anyPosition}
            disabled={disabled}
            onChange={event => setAnyPosition(event.target.checked)}
            className="accent-[#1D74D8]"
          />
          New group can use any position
        </label>
      )}

      <div className={`${compact ? 'mt-2 space-y-1' : 'mt-4 space-y-2'}`}>
        {positioned.map((subgroup, index) => (
          <div
            key={subgroup.localKey}
            draggable={!disabled}
            onDragStart={() => setDraggedKey(subgroup.localKey)}
            onDragEnd={() => setDraggedKey(null)}
            onDragOver={event => event.preventDefault()}
            onDrop={() => {
              const fromIndex = subgroups.findIndex(item => item.localKey === draggedKey)
              if (fromIndex >= 0) move(fromIndex, index)
              setDraggedKey(null)
            }}
            className={`grid items-center gap-2 rounded border bg-white px-2 py-2 ${compact ? 'grid-cols-[auto_minmax(0,1fr)_auto_auto_auto_auto_auto]' : 'grid-cols-[auto_minmax(160px,1fr)_8rem_6rem_8rem_auto_auto_auto]'} ${draggedKey === subgroup.localKey ? 'border-[#1D74D8] opacity-60' : 'border-[#D6E4F2]'}`}
          >
            <span className="cursor-grab select-none text-gray-400" aria-hidden="true">⋮⋮</span>
            <input
              type="text"
              value={subgroup.name}
              disabled={disabled}
              onChange={event => update(subgroup.localKey, { name: event.target.value })}
              aria-label={`${subgroup.name || 'Unnamed'} group name`}
              className="min-w-0 rounded border border-transparent px-1 py-1 text-sm font-medium text-[#1D5176] hover:border-[#B7C6D8] focus:border-[#1D74D8] focus:outline-none"
            />
            {!compact && (
              <label className="flex items-center gap-2 whitespace-nowrap text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={subgroup.positionMode === 'any'}
                  disabled={disabled}
                  onChange={event => update(subgroup.localKey, {
                    positionMode: event.target.checked ? 'any' : 'fixed',
                    length: event.target.checked ? normalizedDigits : 1,
                  })}
                  aria-label={`${subgroup.name} Any position`}
                  className="accent-[#1D74D8]"
                />
                Any position
              </label>
            )}
            <label className="flex items-center gap-1 text-xs text-gray-500">
              <span className={compact ? 'sr-only' : ''}>Digits</span>
              <input
                type="number"
                min={1}
                max={normalizedDigits}
                value={subgroup.length}
                disabled={disabled || subgroup.positionMode === 'any'}
                onChange={event => update(subgroup.localKey, {
                  length: Math.max(1, Math.min(normalizedDigits, Number(event.target.value) || 1)),
                })}
                aria-label={`${subgroup.name} digits`}
                className="w-14 rounded border border-[#B7C6D8] px-1 py-1 disabled:bg-gray-100"
              />
            </label>
            <span className="whitespace-nowrap rounded bg-[#EAF2FB] px-2 py-1 text-[11px] font-medium text-[#1D5176]">
              {subgroup.positionMode === 'any'
                ? `Any · Pos 1-${normalizedDigits}`
                : `Pos ${subgroup.start + 1}${subgroup.end > subgroup.start ? `-${subgroup.end + 1}` : ''}`}
            </span>
            <button type="button" disabled={disabled || index === 0} onClick={() => move(index, index - 1)} className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30" aria-label={`Move ${subgroup.name} up`}>
              ↑
            </button>
            <button type="button" disabled={disabled || index === subgroups.length - 1} onClick={() => move(index, index + 1)} className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30" aria-label={`Move ${subgroup.name} down`}>
              ↓
            </button>
            <button type="button" disabled={disabled} onClick={() => onChange(subgroups.filter(item => item.localKey !== subgroup.localKey))} className="rounded p-1 text-[#B3261E] hover:bg-red-50 disabled:opacity-30" aria-label={`Remove ${subgroup.name}`}>
              ×
            </button>
          </div>
        ))}
      </div>

      <div className={`mt-3 flex items-start justify-between gap-3 text-xs ${errors.length === 0 ? 'text-green-700' : 'text-[#B3261E]'}`}>
        <span>{hasFixedGroups ? `${fixedLength} of ${normalizedDigits} fixed positions assigned` : 'All positions are available to Any-position groups'}</span>
        {errors.length > 0 && <span role="alert" className="text-right">{errors[0]}</span>}
      </div>
    </div>
  )
}

export default CompositeGroupEditor
